import { Fragment, useState } from "react";
import { Check, Info, Loader2, X } from "lucide-react";
import type { Proposal } from "../api/types";

interface Props {
  proposals: Proposal[];
  onValidate: (proposalId: string) => Promise<unknown> | void;
  onReject: (proposalId: string) => Promise<unknown> | void;
  validating?: string | null;
  rejecting?: string | null;
}

/**
 * The contenu (tableau) of a commande_sortante panneau.
 *
 * Two layouts, gated on **container width** (the panneau's contenu div
 * carries `@container`) — not the viewport. That way a desktop in
 * split-view, where each panneau is half-width, gets the same
 * compact treatment as a phone:
 *   - Wide container (@lg, ≥32rem): full table — Produit, Quantité,
 *     Livraison, Origine, Refuser.
 *   - Narrow container (<@lg): only Produit + Quantité + Refuser fit
 *     comfortably. Livraison + Origine are folded behind a per-row "i"
 *     button that expands an inline detail block underneath the row.
 *
 * Each row stays a `<tr>` in both modes so the table semantics survive;
 * the hidden columns get `hidden @lg:table-cell` and a synthesized
 * compact-mode details button + expanded row take their place.
 */
export function CommandeTableau({
  proposals,
  onValidate,
  onReject,
  validating,
  rejecting,
}: Props) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Filter to commande_line only — the panneau's `pending_proposals`
  // bucket also carries Phase-7b panneau-targeted proposals
  // (note_add, label_attach, attachable_attach) so the chips
  // upstream can render their own ghosts. Without this filter those
  // leak into the table as zero-quantity rows.
  const lineProposals = proposals.filter((p) => p.type === "commande_line");

  if (lineProposals.length === 0) {
    return (
      <div className="flex h-full min-h-[16rem] items-center justify-center text-muted dark:text-muted-dark text-sm">
        Aucune proposition en attente. Tapez un message à l'agent en haut de l'écran.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm table-fixed">
        {/* Wide (@lg): 5 cols (Produit 32%, Quantité 16, Livraison 24,
            Origine 1fr, Refuser 20). Narrow: only Produit (1fr),
            Quantité (16), Détails (10), Refuser (20) are visible. */}
        <colgroup>
          <col />
          <col className="w-12 @lg:w-16" />
          <col className="hidden @lg:table-column @lg:w-24" />
          <col className="hidden @lg:table-column" />
          <col className="w-10 @lg:hidden" />
          <col className="w-16 @lg:w-20" />
        </colgroup>
        <thead>
          <tr className="text-left text-muted dark:text-muted-dark font-normal border-b border-black/10 dark:border-white/10">
            <th className="py-2 pr-3 font-normal">Produit</th>
            <th className="py-2 pr-3 font-normal text-right">Qté</th>
            <th className="hidden @lg:table-cell py-2 pr-3 font-normal">
              Livraison
            </th>
            <th
              className="hidden @lg:table-cell py-2 pr-3 font-normal"
              title="Phrase du message qui a généré cette proposition"
            >
              Origine
            </th>
            <th className="@lg:hidden py-2 font-normal" aria-label="Détails" />
            <th className="py-2 font-normal text-right"></th>
          </tr>
        </thead>
        <tbody>
          {lineProposals.map((p) => {
            const pl = p.payload;
            const isValidating = validating === p.id;
            const isRejecting = rejecting === p.id;
            const isExpanded = expandedId === p.id;
            return (
              <Fragment key={p.id}>
                <tr className="border-b border-black/5 dark:border-white/10 last:border-0 align-top">
                  <td
                    className="py-2 pr-3 font-medium truncate"
                    title={String(pl.canonical_id ?? "")}
                  >
                    {String(pl.canonical_id ?? "")}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums whitespace-nowrap">
                    {Number(pl.quantity ?? 0)}
                  </td>
                  <td className="hidden @lg:table-cell py-2 pr-3 text-muted dark:text-muted-dark tabular-nums whitespace-nowrap">
                    {pl.delivery_date ? String(pl.delivery_date) : "—"}
                  </td>
                  <td className="hidden @lg:table-cell py-2 pr-3 text-muted dark:text-muted-dark italic break-words">
                    « {p.source_quote} »
                  </td>
                  {/* Compact-mode details toggle (hidden once container is wide) */}
                  <td className="@lg:hidden py-2 text-right">
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedId((cur) => (cur === p.id ? null : p.id))
                      }
                      title="Voir les détails"
                      aria-expanded={isExpanded}
                      aria-label="Voir les détails"
                      className={`inline-flex items-center justify-center h-7 w-7 rounded-full ${
                        isExpanded
                          ? "bg-ink text-white"
                          : "text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark hover:bg-black/5 dark:hover:bg-white/5"
                      }`}
                    >
                      <Info className="h-3.5 w-3.5" />
                    </button>
                  </td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => onValidate(p.id)}
                      disabled={isValidating || isRejecting}
                      title="Valider cette ligne"
                      className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-ink dark:text-ink-dark hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-40 mr-1"
                      aria-label={`Valider ${pl.canonical_id}`}
                    >
                      {isValidating ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Check className="h-3 w-3" />
                      )}
                      <span className="hidden @lg:inline">Valider</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => onReject(p.id)}
                      disabled={isValidating || isRejecting}
                      title="Refuser cette ligne"
                      className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-40"
                      aria-label={`Refuser ${pl.canonical_id}`}
                    >
                      {isRejecting ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <X className="h-3 w-3" />
                      )}
                      <span className="hidden @lg:inline">Refuser</span>
                    </button>
                  </td>
                </tr>
                {/* Expanded detail row, only meaningful in compact
                    layout. We render it inside the <tbody> so the table
                    semantics stay correct; colSpan covers all visible
                    compact-mode columns. */}
                {isExpanded && (
                  <tr className="@lg:hidden bg-canvas/60 dark:bg-canvas-dark/60">
                    <td colSpan={4} className="px-2 py-2 text-xs space-y-1">
                      <div>
                        <span className="text-muted dark:text-muted-dark">Livraison : </span>
                        <span className="tabular-nums">
                          {pl.delivery_date ? String(pl.delivery_date) : "—"}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted dark:text-muted-dark">Client : </span>
                        <span>{String(pl.client ?? "—")}</span>
                      </div>
                      <div className="text-muted dark:text-muted-dark italic break-words">
                        « {p.source_quote} »
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
